import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { useRef } from 'react';
import {
  getActiveEmotionBlendshapesByMesh,
  getCoreEmotionConfidenceScale,
  getEmotionBlendShapeRampSpeed,
  getEmotionMeshLerpSpeed,
  getEmotionOwnedBlendshapeNamesByMesh,
  getEmotionPayloadKey,
  getEmotionTransitionAlpha,
  getPresetMeshName,
  isAvaturnAnimatedFaceMorph,
  isMouthBlendshape,
  isNeutralEmotionPayload,
  isSpeechSafeMouthBlendshape,
  resolveEmotionPresets,
  scaleEmotionBlendshapes,
  smoothEmotionBlendshapesByMesh,
  stripAvaturnAnimatedBlendshapes
} from '../helpers/emotion';

/** Per-frame emotion overlay: preset resolution, lipsync-safe mouth handling, neutral release. */
export const useEmotionOverlay = ({
  client,
  scene,
  renderRoot,
  enabled = true,
  engineType,
  scaleByConfidence = false
}) => {
  const smoothedBlendshapesByMeshRef = useRef({});
  const wasTalkingRef = useRef(false);
  const lastOverlayEmotionKeyRef = useRef('');

  useFrame((_, delta) => {
    const morphRoot = renderRoot?.current ?? scene;
    if (!enabled || !morphRoot) return;

    const isTalking = !!client?.isTalking;
    const justStoppedTalking = wasTalkingRef.current && !isTalking;
    wasTalkingRef.current = isTalking;

    const emotionPayload = client?.emotion;
    const overlayEmotionKey = getEmotionPayloadKey(emotionPayload);
    const emotionJustChanged =
      overlayEmotionKey !== lastOverlayEmotionKeyRef.current;
    if (emotionJustChanged) {
      lastOverlayEmotionKeyRef.current = overlayEmotionKey;
      smoothedBlendshapesByMeshRef.current = {};
    }

    let presets = resolveEmotionPresets(emotionPayload, { engineType });

    let targetByMesh = getActiveEmotionBlendshapesByMesh(presets, {
      isTalking,
      engineType
    });

    if (presets.length) {
      const confidenceScale = scaleByConfidence
        ? getCoreEmotionConfidenceScale(emotionPayload)
        : 1;

      targetByMesh = Object.fromEntries(
        Object.entries(targetByMesh).map(([meshName, blendshapes]) => [
          meshName,
          stripAvaturnAnimatedBlendshapes(
            scaleEmotionBlendshapes(blendshapes, confidenceScale),
            meshName
          )
        ])
      );
    }

    const rampAlpha = getEmotionTransitionAlpha({
      delta,
      speed: getEmotionBlendShapeRampSpeed({ isTalking, engineType })
    });

    smoothedBlendshapesByMeshRef.current = smoothEmotionBlendshapesByMesh({
      previousByMesh: smoothedBlendshapesByMeshRef.current,
      targetByMesh,
      isTalking,
      justStoppedTalking,
      rampAlpha
    });

    const activeBlendshapesByMesh = smoothedBlendshapesByMeshRef.current;
    const meshLerpAlpha = getEmotionTransitionAlpha({
      delta,
      speed: getEmotionMeshLerpSpeed({ isTalking })
    });
    const shouldReleaseEmotion =
      !presets.length && isNeutralEmotionPayload(emotionPayload);
    const activeMeshNames = new Set(presets.map(getPresetMeshName));
    const ownedBlendshapesByMesh = getEmotionOwnedBlendshapeNamesByMesh();

    morphRoot.traverse((child) => {
      if (
        !child.isSkinnedMesh ||
        !child.morphTargetDictionary ||
        !child.morphTargetInfluences
      ) {
        return;
      }

      const meshName = child.name;
      const activeBlendshapes = activeBlendshapesByMesh[meshName] ?? {};
      const isActiveMesh = activeMeshNames.has(meshName);

      if (!isActiveMesh && !shouldReleaseEmotion) return;

      const releasableNames = shouldReleaseEmotion
        ? (ownedBlendshapesByMesh[meshName] ?? [])
        : isActiveMesh
          ? Object.keys(child.morphTargetDictionary)
          : [];

      releasableNames.forEach((morphName) => {
        if (activeBlendshapes[morphName] !== undefined) return;
        if (
          isTalking &&
          isMouthBlendshape(morphName) &&
          !isSpeechSafeMouthBlendshape(morphName)
        ) {
          return;
        }
        if (isAvaturnAnimatedFaceMorph(morphName, meshName)) return;

        const index = child.morphTargetDictionary[morphName];
        if (index === undefined) return;

        child.morphTargetInfluences[index] = THREE.MathUtils.lerp(
          child.morphTargetInfluences[index],
          0,
          meshLerpAlpha
        );
      });

      Object.entries(activeBlendshapes).forEach(([key, value]) => {
        if (isAvaturnAnimatedFaceMorph(key, meshName)) return;
        if (
          isTalking &&
          isMouthBlendshape(key) &&
          !isSpeechSafeMouthBlendshape(key)
        ) {
          return;
        }

        const index = child.morphTargetDictionary[key];
        if (index === undefined) return;

        if (isTalking && isSpeechSafeMouthBlendshape(key)) {
          child.morphTargetInfluences[index] = value;
        } else {
          child.morphTargetInfluences[index] = THREE.MathUtils.lerp(
            child.morphTargetInfluences[index],
            value,
            meshLerpAlpha
          );
        }
      });
    });
  }, -1);
};
